import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { InformeResponse } from '@/types';
import { useTusInformes } from '@/hooks/useTusInformes';
import { FiltrosInformes } from '@/components/ui/FiltrosInformes';
import { ListaInformes } from '@/components/ui/ListaInformes';
import { DetalleInforme } from '@/components/ui/DetalleInforme';
import { FormularioInformeModal } from '@/components/ui/FormularioInformeModal';

/**
 * Página para mostrar y gestionar los informes del entrenador
 */
const TusInformes: React.FC = () => {
  const {
    informes,
    loading,
    error,
    filtros,
    actualizarFiltros,
    limpiarFiltros,
    actualizarInforme,
    totalInformes,
    informesFiltrados,
    obtenerInformes,
    hayFiltrosActivos,
    mostrandoLimitado,
    loadMore,
    hasMore
  } = useTusInformes();
  
  const [informeSeleccionado, setInformeSeleccionado] = useState<InformeResponse | null>(null);
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);

  /**
   * Maneja la selección de un informe
   */
  const handleSeleccionarInforme = (informe: InformeResponse) => {
    setInformeSeleccionado(informe);
  };

  /**
   * Maneja volver desde el detalle
   */
  const handleVolver = () => {
    setInformeSeleccionado(null);
  };

  /**
   * Maneja la actualización de un informe - CORREGIDO: Ahora retorna Promise<InformeResponse>
   */
  const handleActualizarInforme = async (id: number, data: Partial<InformeResponse>): Promise<InformeResponse> => {
    try {
      const informeActualizado = await actualizarInforme(id, data);
      setInformeSeleccionado(null);
      return informeActualizado;
    } catch (error) {
      console.error('Error al actualizar informe:', error);
      throw error; // Re-lanzar el error para que el componente DetalleInforme lo maneje
    }
  };

  /**
   * Maneja abrir el modal de crear informe
   */
  const handleAbrirModalCrear = () => {
    setMostrarModalCrear(true);
  };

  /**
   * Maneja cerrar el modal de crear informe
   */
  const handleCerrarModalCrear = () => {
    setMostrarModalCrear(false);
  };

  /**
   * Maneja el éxito al crear un informe
   */
  const handleInformeCreado = () => {
    // Recargar la lista de informes sin recargar la página
    obtenerInformes();
  };

  /**
   * Maneja mostrar todos los informes
   */
  const handleMostrarTodos = () => {
    actualizarFiltros({ mostrarTodos: true });
  };

  /**
   * Maneja volver a mostrar solo los últimos 10
   */
  const handleMostrarRecientes = () => {
    actualizarFiltros({ mostrarTodos: false });
  };

  // Si hay un informe seleccionado, mostrar el detalle
  if (informeSeleccionado) {
    return (
      <div className="min-h-screen bg-secondary-900 p-4">
        <DetalleInforme
          informe={informeSeleccionado}
          onVolver={handleVolver}
          onActualizar={handleActualizarInforme}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-900 p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary-50 mb-2">
          Tus Informes
        </h1>
        <p className="text-secondary-400">
          Gestiona y revisa todos tus informes de jugadores
        </p>
      </div>

      {/* Filtros - CORREGIDO: Añadidas las propiedades faltantes */}
      <div className="mb-6">
        <FiltrosInformes
          filtros={filtros}
          onFiltrosChange={actualizarFiltros}
          onLimpiarFiltros={limpiarFiltros}
          totalInformes={totalInformes}
          informesFiltrados={informesFiltrados}
        />
      </div>

      {/* Información de resultados */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <p className="text-secondary-400 text-sm">
            {mostrandoLimitado ? (
              <>
                Mostrando los últimos <span className="text-secondary-200 font-medium">{informesFiltrados}</span> de <span className="text-secondary-200 font-medium">{totalInformes}</span> informes
              </>
            ) : (
              <>
                Mostrando <span className="text-secondary-200 font-medium">{informesFiltrados}</span> de <span className="text-secondary-200 font-medium">{totalInformes}</span> informes
              </>
            )}
          </p>
          
          {mostrandoLimitado && (
            <button
              onClick={handleMostrarTodos}
              className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
            >
              Ver todos
            </button>
          )}
          
          {filtros.mostrarTodos && !hayFiltrosActivos && (
            <button
              onClick={handleMostrarRecientes}
              className="text-secondary-400 hover:text-secondary-300 text-sm font-medium transition-colors"
            >
              Ver solo recientes
            </button>
          )}
        </div>
        
        {(hayFiltrosActivos || filtros.mostrarTodos) && (
          <button
            onClick={limpiarFiltros}
            className="text-secondary-400 hover:text-secondary-200 text-sm transition-colors"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Lista de informes - CORREGIDO: Removida prop error y añadida onVolver */}
      <ListaInformes
        informes={informes}
        loading={loading}
        onSeleccionarInforme={handleSeleccionarInforme}
        onLoadMore={loadMore}
        hasMore={hasMore}
      />

      {/* Botón flotante para crear informe */}
      {!loading && (
        <button
          onClick={handleAbrirModalCrear}
          className="fixed bottom-24 right-6 w-14 h-14 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center shadow-lg transition-colors z-50"
          aria-label="Crear nuevo informe"
        >
          <Plus className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Modal para crear informe */}
      <FormularioInformeModal
        isOpen={mostrarModalCrear}
        onClose={handleCerrarModalCrear}
        onSuccess={handleInformeCreado}
      />
    </div>
  );
};

export default TusInformes;
